import {
   trackAnalyticsCartUpdate,
   trackInAnalyticsAddToCart,
   useShopifyAddToCartTracker,
} from '@ifixit/analytics';
import { useTranslations } from '@ifixit/i18n/hooks';
import {
   type CartWarningFieldsFragment,
   type CountryCode,
   type CartUserErrorFieldsFragment,
   type LanguageCode,
   StorefrontClient,
   useShopifyStorefrontClient,
} from '@ifixit/shopify-storefront-client';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import {
   convertAddedLinesToShopifyAnalyticsEvent,
   convertAddToCartInputToAnalyticsItemEvent,
   convertCartLineItemsToAnalyticsItem,
   trackAnalyticsShopifyAddToCart,
} from '../../helpers/analytics';
import { toCartLineInput } from '../../helpers/cart-line-input';
import {
   addToCart,
   getCart,
   isCartNotFoundError,
   removeCartLines,
   updateCartLines,
} from '../../helpers/storefront-api';
import { buildIfixitCart } from '../../models/cart';
import { CartMutationType, type Cart, type CartLineItem } from '../../types';
import { useCart, useCartToasts } from './use-cart';
import { useCreateCart } from './use-create-cart';
import { useCartKeys } from './use-cart-keys';
import { sendReactNativeMessage } from '@ifixit/utils';

export type AddToCartDescriptor =
   | 'PDP Main'
   | 'PDP Featured Products'
   | 'Featured Products'
   | 'PDP Floating Bar'
   | 'Cart Page Cross Sell'
   | 'Cart Page Merch'
   | 'Cart Drawer Cross Sell'
   | 'Cart Drawer Merch'
   | 'Frequently Bought Together'
   | 'Guide Products'
   | 'Guide Step'
   | 'Answers Product Ad'
   | 'Product Ad Cards - Answers'
   | 'Product Ad Cards - Guide'
   | 'Device Parts and Tools'
   | 'Buy it Again'
   | 'Cart Permalink'
   | 'Product Comparison'
   | 'Fixbot Subscription';

/**
 * The product list the lines were added from and their position in it, so
 * GA4 credits the add to cart and the purchase to that list.
 */
type AddToCartItemList = { id: string; name: string; index: number };

type AddToCartAnalytics =
   | {
        descriptor: Exclude<AddToCartDescriptor, 'Frequently Bought Together'>;
        localeCode?: string;
        itemList?: AddToCartItemList;
     }
   | {
        descriptor: Extract<AddToCartDescriptor, 'Frequently Bought Together'>;
        currentItemcode: string;
        localeCode?: string;
        itemList?: AddToCartItemList;
     };

export type AddToCartInput = {
   lines: CartLineItem[];
   analytics: AddToCartAnalytics;
};

export function useAddToCart() {
   const { mutationKey, queryKey } = useCartKeys();
   const cart = useCart().data;
   const createCart = useCreateCart();
   const isMutating = useIsMutating({
      mutationKey,
      predicate: mutation => mutation.meta?.type !== CartMutationType.AddToCart,
   });
   const queryClient = useQueryClient();
   const trackShopifyAddToCart = useShopifyAddToCartTracker();
   const {
      client: storefrontClient,
      country,
      currencyCode,
      language,
   } = useShopifyStorefrontClient();
   const t = useTranslations();
   const { showCartWarnings, showErrorToast } = useCartToasts({
      title: t('ErrorHandling.unableToAddProduct'),
   });
   const mutation = useMutation({
      mutationKey,
      meta: { type: CartMutationType.AddToCart },
      mutationFn: async ({ lines, analytics }: AddToCartInput) => {
         if (lines.length === 0 || cart == null) {
            return buildIfixitCart({
               cart: null,
               fallbackCurrencyCode: currencyCode,
               userErrors: [],
               validationErrors: [],
               warnings: [],
            });
         }

         let newCart = null;
         let userErrors: CartUserErrorFieldsFragment[] = [];
         let validationErrors: string[] = [];
         let warnings: CartWarningFieldsFragment[] = [];

         if (cart.shopifyCartId) {
            const result = await performAddToCart({
               addedFrom: analytics.descriptor,
               cartId: cart.shopifyCartId,
               client: storefrontClient,
               country,
               lines,
               language,
            });
            newCart = result.cart;
            userErrors = result.userErrors;
            validationErrors = result.validationErrors;
            warnings = result.warnings;
         }

         if (!cart.shopifyCartId || !newCart) {
            const result = await createCart({
               country,
               lines: lines.map(line => toCartLineInput(line, analytics.descriptor)),
               language,
            });
            newCart = result.cart;
            userErrors = result.userErrors;
            validationErrors = result.validationErrors;
            warnings = result.warnings;
         }

         return buildIfixitCart({
            cart: newCart,
            fallbackCurrencyCode: currencyCode,
            userErrors,
            validationErrors,
            warnings,
         });
      },
      onMutate: async ({ lines }: AddToCartInput) => {
         await queryClient.cancelQueries({ queryKey });
         window.onbeforeunload = () =>
            "Some products are being added to the cart. Are you sure you'd like to cancel?";

         const previousCart = queryClient.getQueryData<Cart>(queryKey);

         queryClient.setQueryData<Cart | undefined>(queryKey, currentCart => {
            if (currentCart == null) {
               return currentCart;
            }
            const updatedCart = lines.reduce((cart, line) => addLineItem(cart, line), currentCart);

            return updatedCart;
         });

         return { previousCart };
      },
      onError: (error, variables, context) => {
         queryClient.setQueryData<Cart | undefined>(queryKey, context?.previousCart);
         showErrorToast();
      },
      onSuccess: async (cart, variables) => {
         // Only update the cart state if we are the only mutation running.
         if (queryClient.isMutating({ mutationKey }) === 1) {
            queryClient.setQueryData<Cart | undefined>(queryKey, cart);
         }
         showCartWarnings({ cart, type: CartMutationType.AddToCart, variables });

         trackAnalyticsShopifyAddToCart(variables);
         const event = convertAddToCartInputToAnalyticsItemEvent(variables, cart.shopifyCartId);
         trackInAnalyticsAddToCart(event);
         trackAnalyticsCartUpdate({
            items: convertCartLineItemsToAnalyticsItem(cart.lineItems),
            value: Number(cart.totals.price.amount),
            currency: cart.totals.price.currencyCode,
            localeCode: variables.analytics.localeCode,
            shopifyCartId: cart.shopifyCartId,
         });
         if (trackShopifyAddToCart && cart.shopifyCartId) {
            trackShopifyAddToCart(
               convertAddedLinesToShopifyAnalyticsEvent(variables.lines, cart.shopifyCartId)
            );
         }
         sendReactNativeMessage({ type: 'cart_updated', total: cart.totals.itemsCount });
      },
      onSettled: () => {
         window.onbeforeunload = () => {};
      },
   });
   return { addToCart: mutation, enabled: cart != null && !isMutating };
}

export async function performAddToCart({
   addedFrom,
   cartId,
   client,
   country,
   lines,
   language,
}: {
   /** Tags the new lines; without it each line keeps its own `addedFrom`. */
   addedFrom?: AddToCartDescriptor;
   client: StorefrontClient;
   cartId: string;
   country: CountryCode;
   lines: CartLineItem[];
   language: LanguageCode;
}) {
   let cart = await getCart(client, { cartId, country, language });
   let lineEdges = cart?.lines?.edges ?? [];

   // For subscription items (those with a sellingPlanId), remove any existing
   // cart line for the same product but a different variant. This prevents
   // having both monthly and yearly subscription variants in the cart.
   const conflictingLineIds = lines
      .filter(line => line.sellingPlanId)
      .flatMap(line =>
         lineEdges
            .filter(
               ({ node }) =>
                  node.merchandise.product.id === line.shopifyProductId &&
                  node.merchandise.id !== line.shopifyVariantId
            )
            .map(({ node }) => node.id)
      );

   if (conflictingLineIds.length > 0) {
      const removeResult = await removeCartLines(client, {
         cartId,
         country,
         lineIds: conflictingLineIds,
         language,
      });
      if (isCartNotFoundError(removeResult.userErrors)) {
         return { cart: null, userErrors: [], validationErrors: [], warnings: [] };
      }
      cart = removeResult.cart ?? null;
      lineEdges = cart?.lines?.edges ?? [];
   }

   // A variant already in the cart gets more quantity on its line, and the
   // line keeps the `_added_from` it has: the add that put the product in the
   // cart gets the sale. A second line per source would show the product
   // twice in the cart.
   const linesToUpdate =
      cart == null
         ? []
         : lines
              .map(line => {
                 const existingLine = lineEdges.find(
                    ({ node }) => node.merchandise.id === line.shopifyVariantId
                 )?.node;
                 const existingQuantity = existingLine?.quantity ?? 0;
                 return existingLine?.id
                    ? {
                         id: existingLine.id,
                         merchandiseId: line.shopifyVariantId,
                         quantity: existingQuantity + line.quantity,
                      }
                    : null;
              })
              .filter(<T,>(line: T | null): line is T => line != null);
   const linesToAdd = lines
      .filter(line => !lineEdges.some(({ node }) => node.merchandise.id === line.shopifyVariantId))
      .map(line => toCartLineInput(line, addedFrom));

   let newCart = cart;
   let userErrors: CartUserErrorFieldsFragment[] = [];
   let validationErrors: string[] = [];
   let warnings: CartWarningFieldsFragment[] = [];

   if (linesToUpdate.length > 0) {
      const result = await updateCartLines(client, {
         cartId,
         lines: linesToUpdate,
         country,
         language,
      });
      newCart = result.cart ?? null;
      userErrors = [...userErrors, ...result.userErrors];
      validationErrors = [...validationErrors, ...result.validationErrors];
      warnings = [...warnings, ...result.warnings];
   }

   if (linesToAdd.length > 0) {
      const result = await addToCart(client, { cartId, lines: linesToAdd, country, language });
      newCart = result.cart ?? null;
      userErrors = [...userErrors, ...result.userErrors];
      validationErrors = [...validationErrors, ...result.validationErrors];
      warnings = [...warnings, ...result.warnings];
   }

   // If the cart was deleted (e.g. after checkout or expiration), return null
   // with no errors so the caller can create a fresh cart with the items.
   if (isCartNotFoundError(userErrors)) {
      return { cart: null, userErrors: [], validationErrors: [], warnings: [] };
   }

   newCart ??= userErrors.length > 0 || validationErrors.length > 0 ? cart : null;

   return { cart: newCart, userErrors, validationErrors, warnings };
}

function addLineItem(cart: Cart, inputLineItem: CartLineItem): Cart {
   const currentLineItemIndex = cart.lineItems.findIndex(
      item => item.itemcode === inputLineItem.itemcode
   );
   const isAlreadyInCart = currentLineItemIndex !== -1;
   const updatedLineItems = [...cart.lineItems];

   let priceDelta = inputLineItem.quantity * Number(inputLineItem.price.amount);
   let countDelta = inputLineItem.quantity;

   if (isAlreadyInCart) {
      const currentLineItem = cart.lineItems[currentLineItemIndex];
      if (currentLineItem.shopifyVariantId === inputLineItem.shopifyVariantId) {
         const updatedQuantity = currentLineItem.quantity + inputLineItem.quantity;
         updatedLineItems[currentLineItemIndex] = {
            ...currentLineItem,
            quantity: updatedQuantity,
         };
      } else {
         // Different variant of same product (e.g. switching billing period) — replace entirely
         priceDelta -= currentLineItem.quantity * Number(currentLineItem.price.amount);
         countDelta -= currentLineItem.quantity;
         updatedLineItems[currentLineItemIndex] = inputLineItem;
      }
   } else {
      // Match Shopify's behavior of prependng new items to the cart
      updatedLineItems.unshift(inputLineItem);
   }

   const updatedItemCount = cart.totals.itemsCount + countDelta;
   const updateTotalPrice = Math.max(Number(cart.totals.price.amount) + priceDelta, 0).toFixed(2);
   return {
      ...cart,
      hasItemsInCart: updatedItemCount > 0,
      isEmpty: updatedItemCount === 0,
      lineItems: updatedLineItems,
      totals: {
         ...cart.totals,
         price: {
            ...cart.totals.price,
            amount: updateTotalPrice,
         },
         itemsCount: updatedItemCount,
      },
   };
}
